const MONGO_ID_RE = /^[a-f0-9]{24}$/i

const looksLikeId = (value: string): boolean => MONGO_ID_RE.test(value.trim())

const firstMeaningfulString = (...values: unknown[]): string | null => {
  const match = values.find((value) => typeof value === 'string' && value.trim() && !looksLikeId(value))
  return match ? String(match).trim() : null
}

const hostnameFromUrl = (url?: string): string | null => {
  if (!url || typeof url !== 'string') return null
  const trimmed = url.trim()
  if (!trimmed || looksLikeId(trimmed)) return null

  try {
    const parsed = new URL(trimmed.includes('://') ? trimmed : `https://${trimmed}`)
    const host = parsed.hostname.replace(/^www\./, '')
    if (!host || looksLikeId(host)) return null
    return host
  } catch {
    if (trimmed.includes('.') && !trimmed.includes(' ') && !looksLikeId(trimmed)) {
      return trimmed
    }
    return null
  }
}

const readablePathFromUrl = (url?: string): string | null => {
  if (!url || typeof url !== 'string' || looksLikeId(url)) return null

  try {
    const parsed = new URL(url.includes('://') ? url : `https://${url}`)
    const parts = parsed.pathname.split('/').filter(Boolean).filter(part => !looksLikeId(part))
    if (parts.length === 0) return null
    return `/${parts.slice(-2).join('/')}`
  } catch {
    return null
  }
}

export const formatEndpointLabel = (endpoint: any, role: 'source' | 'destination'): string => {
  const fallback = role === 'source' ? 'Source' : 'Destination'
  if (!endpoint) return fallback

  const name = firstMeaningfulString(
    endpoint.name,
    endpoint.displayName,
    endpoint.connectorName,
    endpoint.system,
    endpoint.databaseConfig?.database,
    endpoint.databaseConfig?.table,
    endpoint.databaseConfig?.collection,
    endpoint.azureConfig?.entityPath
  )
  if (name) return name

  const method = String(endpoint.requestMethod || '').toUpperCase()
  const host = hostnameFromUrl(endpoint.url)
  if (host) return method ? `${method} ${host}` : host

  const path = firstMeaningfulString(endpoint.path, endpoint.route, readablePathFromUrl(endpoint.url))
  if (path) return method ? `${method} ${path}` : path

  const type = firstMeaningfulString(endpoint.type, endpoint.connectorType, endpoint.platform)
  if (type) return type.replace(/[_-]/g, ' ')

  if (method) return `${method} API`
  return `${fallback} configured`
}

/** Queue, topic, event hub, or Pub/Sub — endpoints that have a backlog to measure. */
export const endpointHasQueueDepth = (endpoint: any): boolean => {
  if (!endpoint) return false
  if (endpoint.azureConfig?.entityPath || endpoint.azureConfig?.queueName || endpoint.azureConfig?.topicName) return true
  if (endpoint.azureEventHub?.entityPath) return true
  const pubsub = endpoint.pubsub || endpoint.pubsubConfig
  if (pubsub?.topicOrSubscription || pubsub?.entityPath) return true
  const type = String(endpoint.type || endpoint.connectorType || '').toLowerCase()
  return type.includes('service bus') || type.includes('event hub') || type.includes('pub/sub') || type.includes('pubsub')
}

export const formatQueueSize = (size?: number | null, approximate?: boolean): string => {
  if (typeof size !== 'number' || !Number.isFinite(size)) return '—'
  const rounded = Math.max(0, Math.round(size)).toLocaleString()
  return approximate ? `~${rounded}` : rounded
}

/** Master SoR sync: polls source and upserts masters (no external destination). */
export const isMasterSyncConfig = (connector?: any, config?: any): boolean => {
  const destType = config?.destination?.type || connector?.destination?.type
  return !!(
    connector?.masterSync ||
    config?.masterSync ||
    connector?.supportPackage === 'Master Data Sync' ||
    config?.supportPackage === 'Master Data Sync' ||
    destType === 'Master Data' ||
    destType === 'Internal'
  )
}

export const isInsightsSyncConfig = (connector?: any, config?: any): boolean => {
  // Master Data Sync is not insights-only — it has an internal master upsert path
  if (isMasterSyncConfig(connector, config)) return false

  const dest = config?.destination ?? connector?.destination
  const hasDestination = !!(dest?.url || dest?.type || (dest && Object.keys(dest).length > 0))
  const hasDataMapping = !!(config?.dataMapping?.length || connector?.dataMapping?.length)
  return (
    connector?.integrationMode === 'insights_sync' ||
    (!!connector?.syncCadence && !hasDestination) ||
    (!hasDestination && !hasDataMapping)
  )
}
